import { useQuery } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import ms from 'ms';
import { useEffect } from 'react';

import { toast } from '@hivebrite/design-system/Toast';

import { getMentorProfile } from 'hb-react/frontoffice/apps/Mentoring/requests';
import {
  type MentorProfile,
  type Program,
} from 'hb-react/frontoffice/apps/Mentoring/types';
import {
  getLowerCaseMentorLabel,
  getProgramTopicId,
} from 'hb-react/frontoffice/apps/Mentoring/utils';
import { QUERY_KEYS } from 'hb-react/shared/apps/Mentoring/constants';

interface GetMentorProfileParams {
  mentorId: number;
  program: Program;
}

export const useGetMentorProfile = ({
  program,
  mentorId,
}: GetMentorProfileParams) => {
  const {
    id: programId,
    parent_id: parentId,
    parent_type: parentType,
    mentor_label: mentorLabel,
  } = program;

  const { data, isLoading, error } = useQuery<MentorProfile, AxiosError>({
    queryKey: [QUERY_KEYS.mentorProfile, programId, mentorId],
    queryFn: ({ signal }) =>
      getMentorProfile({
        mentorId,
        programId,
        signal,
        topicId: getProgramTopicId({ parentId, parentType }),
      }),
    staleTime: ms('5m'),
  });

  useEffect(() => {
    if (error?.response?.status === 500) {
      toast.error(__('Sorry, an error occurred'));
    }
    if (data && data.status !== 'accepted') {
      toast.error(
        s__('Mentoring|This %{label} does not exist').i__({
          label: getLowerCaseMentorLabel(mentorLabel),
        }),
      );
    }
  }, [data, mentorLabel, error?.response?.status]);

  return { data, isLoading };
};
